import { apiClient } from "@/services/api"
import type { LoginRequest, LoginResponse, AuthUser } from "@/types/auth"

// JWT decode utility (simple implementation)
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split(".")[1]
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error("Error decoding JWT:", error)
    return null
  }
}

export const authService = {
  // Login user
  async login(username: string, password: string): Promise<AuthUser> {
    const loginData: LoginRequest = { username, password }

    const response = await apiClient.post<LoginResponse>("/auth/login", loginData)

    // Decode JWT to get user info
    const decoded = decodeJWT(response.token)

    const user: AuthUser = {
      id: decoded?.NameId || "1",
      email: decoded?.UniqueName || username,
      name: decoded?.FamilyName || "Admin",
      token: response.token,
    }

    // Store token in localStorage
    localStorage.setItem("auth_token", response.token)
    localStorage.setItem("auth_user", JSON.stringify(user))

    return user
  },

  // Logout user
  logout(): void {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("auth_user")
  },

  // Get current user from localStorage
  getCurrentUser(): AuthUser | null {
    if (typeof window === "undefined") return null
    
    try {
      const token = localStorage.getItem("auth_token")
      const userStr = localStorage.getItem("auth_user")

      if (!token || !userStr) return null

      const user = JSON.parse(userStr)

      // Check if token is expired
      const decoded = decodeJWT(token)
      if (decoded && decoded.exp * 1000 < Date.now()) {
        this.logout()
        return null
      }

      return user
    } catch (error) {
      console.error("Error getting current user:", error)
      this.logout()
      return null
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null
  },

  // Get auth token from localStorage
  getAuthToken(): string | null {
    if (typeof window === "undefined") return null
    return localStorage.getItem("auth_token")
  },
}

// Export getAuthToken as a standalone function for convenience
export const getAuthToken = (): string | null => {
  if (typeof window === "undefined") return null
  return localStorage.getItem("auth_token")
}
